import {
  ExternalCardStatus,
  InlineAlertType,
  ProcessTypeInput,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useGlobalSearchParams,
  useRouter,
} from 'expo-router';
import { isPositiveValue } from 'features/accounts/AccountDetails/AccountDetails.helpers';
import NoTransactions from 'features/accounts/transactions/NoTransactions/NoTransactions';
import {
  isUserCancelled3DS,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import VerifyCardScreen from 'features/moveMoney/screens/dcf/tabapay/VerifyCardScreen';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import TransactionListItem from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { SNACKBAR_DURATION } from 'shared/constants/snackbar';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

import { useExternalCardsDetails } from './hooks/useExternalCardsDetails';

const flowNameForAnalytics = 'card_management';

const ExternalCardDetails = () => {
  const router = useRouter();
  const { cardId } = useGlobalSearchParams<{
    cardId: string;
  }>();

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'externalAccounts',
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: 'external-debit-card-details',
    customAttributes: { flow_name: flowNameForAnalytics },
  });

  const {
    shouldShowBackButton,
    tabaPayEntryFlowType,
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    handleErrorButtonPress,
    formattedAddress,
    top,
    transactions,
    cancelTransferLoading,
    isLoading,
    setUpdatedMessage,
    setIsVerifyCardScreenShown,
    isVerifyCardScreenShown,
    externalCardDetails,
    shouldShowLoader,
    verifyCard,
    combinedErrorScreen,
    cancelTabapayTransfer,
    initTransactionId,
    initTransactionIdWeb,
    removeBottomSheetRef,
    deleteExternalCard,
    updatedMessage,
    errorScreenThreeDs,
    webErrorScreen,
  } = useExternalCardsDetails();

  return isVerifyCardScreenShown &&
    externalCardDetails?.verifyCardActionScreen ? (
    <VerifyCardScreen
      title={externalCardDetails.verifyCardActionScreen.title}
      contentText={externalCardDetails.verifyCardActionScreen.message}
      buttonLabel={
        externalCardDetails.verifyCardActionScreen.button?.buttonText
      }
      tabaPayEntryFlowType={tabaPayEntryFlowType}
      onContinue={verifyCard}
      onBackClick={() => {
        setIsVerifyCardScreenShown(false);
      }}
      illustrationKey={
        externalCardDetails.verifyCardActionScreen?.image as IllustrationKeyType
      }
    />
  ) : (
    <>
      <ScreenTransitionOverlay
        isLoading={shouldShowLoader}
        LoadingComponent={
          <View style={styles.screen} accessibilityLabel="loading indicator">
            <FullScreenLoading isCoinLoader />
          </View>
        }
        ErrorComponent={
          combinedErrorScreen ? (
            <View style={styles.errorHeader}>
              <View style={[styles.closeIconWrapper, { top: top }]}>
                <IconButton
                  iconKey="CLOSE"
                  iconSize={24}
                  iconOnPress={() => {
                    if (
                      (isUserCancelled3DS(combinedErrorScreen) &&
                        errorScreenThreeDs?.processType ===
                          ProcessTypeInput.Transaction) ||
                      webErrorScreen?.processType ===
                        ProcessTypeInput.Transaction
                    ) {
                      void cancelTabapayTransfer({
                        transactionId:
                          initTransactionId.trim() || initTransactionIdWeb,
                      });
                    } else {
                      navigateBack();
                    }
                  }}
                  iconButtonAccessibilityLabel="close"
                />
              </View>
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={combinedErrorScreen.messageHeadline}
                messageBody={combinedErrorScreen.messageBody}
                primaryButton={{
                  title:
                    combinedErrorScreen.primaryButton?.buttonText ||
                    translatedText('cardDetails.close'),
                  onPress: () => {
                    if (combinedErrorScreen.primaryButton?.buttonText) {
                      handleErrorButtonPress(combinedErrorScreen.primaryButton);
                    } else {
                      navigateBack();
                    }
                  },
                }}
                secondaryButton={
                  combinedErrorScreen.secondaryButton
                    ? {
                        title: combinedErrorScreen.secondaryButton.buttonText,
                        isLoading: cancelTransferLoading,
                        onPress: () =>
                          handleErrorButtonPress(
                            combinedErrorScreen.secondaryButton,
                          ),
                      }
                    : undefined
                }
              />
            </View>
          ) : null
        }
      />
      <View style={styles.screen}>
        <Stack.Screen
          options={{
            headerShown: shouldShowBackButton,
            headerBackVisible: shouldShowBackButton,
            title: externalCardDetails?.cardTitle,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                trailingAccessories={
                  <PressableWithDelay
                    onPress={() => removeBottomSheetRef.current?.present()}
                    analyticsId="LinkedCardRemoveClicked"
                    customAttributes={{
                      card_type: 'debit_card',
                      flow_name: flowNameForAnalytics,
                    }}
                  >
                    <BodySmBoldText style={styles.helpButton} numberOfLines={0}>
                      {translatedText('cardDetails.remove')}
                    </BodySmBoldText>
                  </PressableWithDelay>
                }
                primaryOnPress={() => navigateBack()}
                {...props}
              />
            ),
          }}
        />
        <ScrollView style={styles.scrollView}>
          {externalCardDetails?.inlineAlert ? (
            <View style={styles.bottomSheetContainer}>
              <InlineAlert
                alertType={InlineAlertType.Error}
                key={externalCardDetails.inlineAlert.id}
                titleText={externalCardDetails.inlineAlert.title || undefined}
                bodyText={externalCardDetails.inlineAlert.body || undefined}
                button={{
                  label: externalCardDetails.inlineAlert.buttonTitle || '',
                  type: 'SECONDARY_ALT',
                  size: 'SMALL',
                  onPress: () => {
                    setIsVerifyCardScreenShown(true);
                  },
                }}
              />
            </View>
          ) : null}

          <ListItem
            accessibilityLabel="BANK_NAME"
            secondaryCopy={translatedText('cardDetails.bank')}
            primaryCopy={externalCardDetails?.institution.name ?? ''}
            showsChevron={false}
            lineFormat={ListItemLineFormat.LABEL}
          />
          <View style={styles.dividerWrapper}>
            <HairLineDivider />
          </View>
          <ListItem
            accessibilityLabel="ADDRESS"
            primaryCopy={formattedAddress}
            secondaryCopy={translatedText('cardDetails.address')}
            lineFormat={ListItemLineFormat.LABEL}
            showsChevron={false}
            showsEditButton={true}
            onPress={() =>
              router.push({
                pathname: '/myvaro/external-accounts/[cardId]/update-address',
                params: { cardId },
              })
            }
            analyticsId="EditAddressClicked"
            customAttributes={{ flow_name: flowNameForAnalytics }}
          />
          <View style={styles.dividerWrapper}>
            <HairLineDivider />
          </View>
          <ListItem
            accessibilityLabel="EXP_DATE"
            primaryCopy={translatedText('cardDetails.expDate')}
            trailingAccessory={
              externalCardDetails &&
              externalCardDetails.tag &&
              externalCardDetails.tag.text &&
              externalCardDetails.status === ExternalCardStatus.Expired
                ? {
                    id: externalCardDetails.tag.id,
                    name: VXLComponentId.TAG,
                    props: {
                      text: externalCardDetails.tag?.text,
                      type: TagType.ERROR,
                    },
                  }
                : undefined
            }
            showsChevron={false}
            showsEditButton={true}
            onPress={() =>
              router.push({
                pathname: '/myvaro/external-accounts/update-expiry-date',
                params: {
                  cardId,
                  tabaPayEntryFlowType: TabaPayEntryFlowType.ExternalAccount,
                },
              })
            }
            analyticsId="CardExpirationDateEditClicked"
            customAttributes={{
              card_type: 'debit_card',
              flow_name: flowNameForAnalytics,
            }}
          />

          <View style={styles.divider} />
          {transactions && transactions.length > 0 ? (
            transactions.map((data, index) => {
              return (
                <View key={data.id}>
                  <SectionDivider labelText={data.title.copy} />
                  {data.children.map((item, i) => {
                    return (
                      <View key={item.id}>
                        {i > 0 ? (
                          <View style={styles.dividerWrapper}>
                            <HairLineDivider />
                          </View>
                        ) : null}
                        <TransactionListItem
                          key={item.id}
                          amountStyle={isPositiveValue(item.primaryDetail.copy)}
                          isPending={
                            data.title.copy ===
                            translatedText('cardDetails.pendingTransactions')
                          }
                          isDisabled={
                            data.title.copy ===
                            translatedText('cardDetails.pendingTransactions')
                          }
                          titleText={item.primaryText.copy}
                          amountText={item.primaryDetail.copy}
                          accessibilityLabel="transfer-list-item"
                          secondaryText={item.secondaryText?.copy}
                        />
                      </View>
                    );
                  })}
                  {transactions.length > 1 &&
                  index !== transactions.length - 1 ? (
                    <View style={styles.divider} />
                  ) : null}
                </View>
              );
            })
          ) : (
            <NoTransactions
              title={translatedText('cardDetails.recentTransactions')}
            />
          )}
          <BottomSheet
            ref={removeBottomSheetRef}
            style={styles.bottomSheetContainer}
          >
            <View style={styles.contentContainer}>
              <TitleMdText>
                {externalCardDetails?.removeExternalCardInfo?.title}
              </TitleMdText>
              <BodyBaseText>
                {externalCardDetails?.removeExternalCardInfo?.message}
              </BodyBaseText>
            </View>
            <View style={styles.buttonGroup}>
              <Button
                type="PRIMARY"
                label={translatedText('cardDetails.removeCard')}
                isLoading={isLoading}
                analyticsId="LinkedCardRemoveConfirmed"
                customAttributes={{
                  card_type: 'debit_card',
                  flow_name: flowNameForAnalytics,
                }}
                onPress={() =>
                  deleteExternalCard(
                    { externalCardId: cardId },
                    {
                      tabaPayEntryFlowType,
                      dueDate,
                      feeAmount,
                      amountId,
                      advanceAmount,
                      repaymentDate,
                      selectedAccountId,
                      paymentSource,
                      selectedAccountName,
                    },
                  )
                }
              />
              <Button
                type="SECONDARY"
                label={translatedText('cardDetails.cancel')}
                analyticsId="LinkedCardRemoveCancelled"
                customAttributes={{
                  card_type: 'debit_card',
                  flow_name: flowNameForAnalytics,
                }}
                onPress={() => removeBottomSheetRef.current?.dismiss()}
              />
            </View>
          </BottomSheet>
        </ScrollView>
        {updatedMessage ? (
          <Snackbar
            text={updatedMessage}
            duration={SNACKBAR_DURATION}
            onHide={() => setUpdatedMessage('')}
          />
        ) : null}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  bottomSheetContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  buttonGroup: {
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  closeIconWrapper: {
    left: theme.spacing.xs,
    position: 'absolute',
    zIndex: 10,
  },
  contentContainer: {
    gap: theme.spacing.s,
    paddingBottom: theme.spacing.m,
  },
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  dividerWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  errorHeader: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  helpButton: {
    color: theme.color.interactive.primary,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.xs,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollView: {
    flexDirection: 'column',
    gap: theme.spacing.s,
  },
});

export default ExternalCardDetails;
